import { FunctionComponent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import {
	faBan,
	faBuilding,
	faClock,
	faEllipsis,
	faRotateLeft,
	faUserSlash,
} from "@fortawesome/pro-regular-svg-icons";

import { FeedPerson, FeedRunCard } from "@signalist/shared-types";

import { Icon, Modal } from "@/components";
import { ButtonColor } from "@/components/Button/types";
import { AssignStatus } from "@/enum/list.enum";
import { classNames } from "@/utils/classNames";

import { useAddProspectsToCrmMutation } from "@/pages/Private/redux/crm/crm.api";
import {
	CrmEntityType,
	CrmProvider,
	CrmSelectionSource,
} from "@/pages/Private/redux/crm/crm.schema";

import { AnchoredPopover } from "@/pages/Private/pages/Contacts/components/LeadPanelActions/AnchoredPopover";
import { useConnectedCrmProviders } from "@/pages/Private/pages/Integrations/hooks/useConnectedCrmProviders";
import { AddToCrmModal, AddToCrmOwner } from "@/pages/Private/components/AddToCrm/AddToCrmModal";

import { FeedUndoToast } from "./FeedUndoToast";

interface RunCardActionsMenuProps {
	card: FeedRunCard;
	/** The card's currently selected person — the menu's actions target this lead. */
	person: FeedPerson | null;
	/** Change the person's lead status — Review later / Back to New / Ignored (page owns it). */
	onChangeStatus?: (card: FeedRunCard, person: FeedPerson, status: AssignStatus) => void;
	/** Untrack the selected person (page owns the mutation + toast). */
	onRemovePerson?: (card: FeedRunCard, person: FeedPerson) => Promise<void>;
	/** Stop tracking the whole company (page owns the mutation + toast). */
	onRemoveCompany?: (card: FeedRunCard) => Promise<void>;
}

type ConfirmKind = "person" | "company";

interface MenuRowProps {
	icon: typeof faClock;
	label: string;
	testId: string;
	variant?: "default" | "danger";
	onClick: () => void;
}

const MenuRow: FunctionComponent<MenuRowProps> = ({
	icon,
	label,
	testId,
	variant = "default",
	onClick,
}) => (
	<button
		className={classNames(
			"flex w-full cursor-pointer items-center gap-2.5 px-3.5 py-2.5 text-left text-[13px] font-medium hover:bg-background-light",
			variant === "danger" ? "text-status-error" : "text-text"
		)}
		data-testid={testId}
		type="button"
		onClick={onClick}
	>
		<Icon className="h-3.5 w-3.5 shrink-0 opacity-70" icon={icon} />
		<span className="truncate">{label}</span>
	</button>
);

interface MenuListProps {
	person: FeedPerson | null;
	companyName: string;
	personName: string;
	onChangeStatus: (status: AssignStatus) => void;
	onAddToCrm: (provider: CrmProvider) => void;
	onRemovePerson: () => void;
	onRemoveCompany: () => void;
}

/**
 * The popover's row list — mounted only while the menu is open so the connected-CRM
 * lookup (feature + OAuth-status queries) never fires for every card on the page.
 */
const MenuList: FunctionComponent<MenuListProps> = ({
	person,
	companyName,
	personName,
	onChangeStatus,
	onAddToCrm,
	onRemovePerson,
	onRemoveCompany,
}) => {
	const { t } = useTranslation();
	const connectedProviders = useConnectedCrmProviders();

	// Placeholder leads (no prospect yet) only expose the removals — status changes and CRM
	// need a real prospect, and untracking needs a tracker_person.
	const isPlaceholder = !!person?.needsProspectSelection;
	const canChangeStatus = !!person?.listPersonId && !isPlaceholder;
	const isSavedForLater = person?.assignStatus === AssignStatus.SAVED_FOR_LATER;
	const isArchived = person?.assignStatus === AssignStatus.ARCHIVED;
	const canAddToCrm = !!person?.prospectId && !isPlaceholder && connectedProviders.length > 0;
	const canRemovePerson = person?.trackerPersonId != null;

	return (
		<div
			className="overflow-hidden rounded-xl border border-border-light bg-white py-1 shadow-lg"
			data-testid="feed-run-actions-menu"
		>
			{/* Status transitions, gated per current state: "Back to New" for anything
			    not already New (To be reviewed *or* Ignored), "Review later" unless
			    already there, and "Move to Ignored" unless already ignored. */}
			{canChangeStatus && (isSavedForLater || isArchived) && (
				<MenuRow
					icon={faRotateLeft}
					label={t("feed.runCard.menu.backToNew")}
					testId="feed-run-action-back-to-new"
					onClick={() => onChangeStatus(AssignStatus.DRAFT)}
				/>
			)}

			{canChangeStatus && !isSavedForLater && (
				<MenuRow
					icon={faClock}
					label={t("feed.runCard.menu.reviewLater")}
					testId="feed-run-action-review-later"
					onClick={() => onChangeStatus(AssignStatus.SAVED_FOR_LATER)}
				/>
			)}

			{canChangeStatus && !isArchived && (
				<MenuRow
					icon={faBan}
					label={t("feed.runCard.menu.moveToIgnored")}
					testId="feed-run-action-move-to-ignored"
					onClick={() => onChangeStatus(AssignStatus.ARCHIVED)}
				/>
			)}

			{canAddToCrm &&
				connectedProviders.map((provider) => (
					<MenuRow
						key={provider}
						icon={faBuilding}
						label={t("feed.runCard.menu.addToProvider", {
							provider: t(`crmAddTo.provider.${provider}`),
						})}
						testId={`feed-run-action-crm-${provider}`}
						onClick={() => onAddToCrm(provider)}
					/>
				))}

			<div className="my-1 border-t border-border-light" />

			{canRemovePerson && (
				<MenuRow
					icon={faUserSlash}
					label={
						personName
							? t("feed.runCard.menu.removePerson", { name: personName })
							: t("feed.runCard.menu.removePersonFallback")
					}
					testId="feed-run-action-remove-person"
					variant="danger"
					onClick={onRemovePerson}
				/>
			)}
			<MenuRow
				icon={faBuilding}
				label={
					companyName
						? t("feed.runCard.menu.stopTrackingCompany", { company: companyName })
						: t("feed.runCard.menu.stopTrackingCompanyFallback")
				}
				testId="feed-run-action-remove-company"
				variant="danger"
				onClick={onRemoveCompany}
			/>
		</div>
	);
};

/**
 * The run-card footer (…) overflow menu (PREC-3635): card-level actions with no
 * dedicated button — Review later (SAVED_FOR_LATER), Add to CRM (per connected
 * provider), and Remove from Profile Tracker at both scopes (the selected person
 * or the whole company). Destructive removals confirm first; the CRM flow reuses
 * the shared {@link AddToCrmModal}. Non-CRM mutations + toasts live on the page.
 */
export const RunCardActionsMenu: FunctionComponent<RunCardActionsMenuProps> = ({
	card,
	person,
	onChangeStatus,
	onRemovePerson,
	onRemoveCompany,
}) => {
	const { t } = useTranslation();
	const [addProspectsToCrm, { isLoading: isAddingToCrm }] = useAddProspectsToCrmMutation();

	const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
	const [crmProvider, setCrmProvider] = useState<CrmProvider | null>(null);
	const [confirm, setConfirm] = useState<ConfirmKind | null>(null);
	const [isRemoving, setIsRemoving] = useState(false);
	const [crmToast, setCrmToast] = useState<{
		message: string;
		variant: "success" | "error";
	} | null>(null);

	useEffect(() => {
		if (!crmToast) {
			return;
		}

		const timer = setTimeout(() => setCrmToast(null), 4000);

		return () => clearTimeout(timer);
	}, [crmToast]);

	const companyName = card.company.name ?? "";
	const personName = person?.name ?? "";

	const closeMenu = () => setAnchorEl(null);

	const handleChangeStatus = (status: AssignStatus) => {
		closeMenu();

		if (person) {
			onChangeStatus?.(card, person, status);
		}
	};

	const handleAddToCrm = (provider: CrmProvider) => {
		closeMenu();
		setCrmProvider(provider);
	};

	const handleSubmitCrm = async (
		provider: CrmProvider,
		entities: CrmEntityType[],
		owner?: AddToCrmOwner
	) => {
		if (!person?.prospectId) {
			return;
		}

		try {
			await addProspectsToCrm({
				provider,
				entities,
				ownerId: owner?.id,
				ownerName: owner?.name,
				selection: {
					source: CrmSelectionSource.CONTACTS,
					selectType: "CURRENT_PAGE",
					ids: [person.prospectId],
				},
			}).unwrap();

			setCrmToast({ message: t("crmAddTo.success"), variant: "success" });
		} catch {
			setCrmToast({ message: t("crmAddTo.error"), variant: "error" });
		} finally {
			setCrmProvider(null);
		}
	};

	const openConfirm = (kind: ConfirmKind) => {
		closeMenu();
		setConfirm(kind);
	};

	const handleConfirmRemoval = async () => {
		if (!confirm) {
			return;
		}

		setIsRemoving(true);

		try {
			if (confirm === "person" && person) {
				await onRemovePerson?.(card, person);
			} else if (confirm === "company") {
				await onRemoveCompany?.(card);
			}
		} finally {
			setIsRemoving(false);
			setConfirm(null);
		}
	};

	const confirmCopy = (): { title: string; body: string; submit: string } => {
		if (confirm === "company") {
			return {
				title: t("feed.runCard.menu.removeCompanyTitle"),
				body: t("feed.runCard.menu.removeCompanyBody", { company: companyName }),
				submit: t("feed.runCard.menu.removeCompanyConfirm"),
			};
		}

		return {
			title: t("feed.runCard.menu.removePersonTitle"),
			body: t("feed.runCard.menu.removePersonBody", { name: personName }),
			submit: t("feed.runCard.menu.removePersonConfirm"),
		};
	};

	const copy = confirmCopy();

	return (
		<>
			<button
				aria-label={t("feed.runCard.menu.trigger")}
				className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-lg border border-border-light text-text-secondary hover:bg-background-light"
				data-testid="feed-run-actions-trigger"
				type="button"
				onClick={(event) =>
					setAnchorEl((current) => (current ? null : (event.currentTarget as HTMLElement)))
				}
			>
				<Icon className="h-3.5 w-3.5" icon={faEllipsis} />
			</button>

			{anchorEl && (
				<AnchoredPopover anchorEl={anchorEl} width={260} onClose={closeMenu}>
					<MenuList
						companyName={companyName}
						person={person}
						personName={personName}
						onAddToCrm={handleAddToCrm}
						onChangeStatus={handleChangeStatus}
						onRemoveCompany={() => openConfirm("company")}
						onRemovePerson={() => openConfirm("person")}
					/>
				</AnchoredPopover>
			)}

			{crmProvider && (
				<AddToCrmModal
					isLoading={isAddingToCrm}
					isOpen={true}
					provider={crmProvider}
					selectedCount={1}
					onClose={() => setCrmProvider(null)}
					onSubmit={handleSubmitCrm}
				/>
			)}

			{confirm && (
				<Modal
					handleClose={() => (isRemoving ? undefined : setConfirm(null))}
					handleSave={handleConfirmRemoval}
					isLoading={isRemoving}
					isOpened={true}
					size="xs"
					submitButtonColor={ButtonColor.DANGER}
					submitButtonText={copy.submit}
					title={copy.title}
				>
					<p className="text-sm text-text-secondary">{copy.body}</p>
				</Modal>
			)}

			{crmToast && <FeedUndoToast message={crmToast.message} variant={crmToast.variant} />}
		</>
	);
};
